import {
  Alert,
  Box,
  Button,
  Cards,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from '@cloudscape-design/components';

import type { VoiceSalesCall, VoiceSalesCampaign, VoiceSalesTarget } from '../../common/api/voiceSales';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { PhoneNumber } from '../../components/PhoneNumber';
import {
  formatComplianceMetadata,
  formatDuration,
  formatSourceLabel,
  VoiceSalesStatus,
} from './presentation';

type QueryState = {
  isLoading: boolean;
  isError: boolean;
  refetch: () => unknown;
};

export type OverviewViewProps = {
  campaigns: VoiceSalesCampaign[];
  targets: VoiceSalesTarget[];
  calls: VoiceSalesCall[];
  campaignsState: QueryState;
  targetsState: QueryState;
  callsState: QueryState;
  onOpenCampaigns: () => void;
  onOpenCalls: () => void;
  onOpenCall: (uuid: string) => void;
};

function sameLocalDay(value: string, today: Date): boolean {
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.toDateString() === today.toDateString();
}

export function OverviewView({
  campaigns,
  targets,
  calls,
  campaignsState,
  targetsState,
  callsState,
  onOpenCampaigns,
  onOpenCalls,
  onOpenCall,
}: OverviewViewProps) {
  const campaignNames = new Map(campaigns.map((campaign) => [campaign.id, campaign.name]));
  const callsToday = calls.filter((call) => sameLocalDay(call.created_at, new Date()));
  const dailyLimit = Math.max(1, ...campaigns.map((campaign) => campaign.daily_cap), 10);
  const remainingCalls = Math.max(0, dailyLimit - callsToday.length);
  const nextTargets = targets
    .filter((target) => ['eligible', 'scheduled'].includes(target.eligibility_status))
    .slice(0, 4);
  const hasError = campaignsState.isError || targetsState.isError || callsState.isError;
  const suppressionChecks = targets.filter((target) => target.metadata_json.dnc_scrubbed_at).length;
  const phoneTypesKnown = targets.filter((target) => target.metadata_json.line_type).length;

  return (
    <SpaceBetween direction="vertical" size="l">
      {hasError ? (
        <Alert
          type="error"
          header="Some overview data could not be loaded"
          action={
            <Button
              onClick={() => {
                if (campaignsState.isError) campaignsState.refetch();
                if (targetsState.isError) targetsState.refetch();
                if (callsState.isError) callsState.refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Retry to refresh campaign, audience, and call activity.
        </Alert>
      ) : null}

      <Container
        header={
          <Header variant="h2" actions={<Button onClick={onOpenCampaigns}>Start a Campaign</Button>}>
            Calls Today
          </Header>
        }
      >
        {callsState.isLoading || campaignsState.isLoading ? (
          <StatusIndicator type="loading">Loading today&apos;s call plan</StatusIndicator>
        ) : (
          <SpaceBetween size="xs">
            <Box fontSize="display-l" fontWeight="bold">
              {`${callsToday.length} / ${dailyLimit}`}
            </Box>
            <Box color="text-body-secondary">{remainingCalls} calls remaining</Box>
          </SpaceBetween>
        )}
      </Container>

      <Cards
        ariaLabels={{
          itemSelectionLabel: (_, target) => formatSourceLabel(target.source_model),
          selectionGroupLabel: 'Next-call queue',
        }}
        items={nextTargets}
        trackBy="uuid"
        loading={targetsState.isLoading}
        loadingText="Loading next-call queue"
        cardsPerRow={[{ cards: 1 }, { minWidth: 700, cards: 2 }]}
        empty={
          <Box textAlign="center">No eligible contacts are waiting. Review a campaign audience to continue.</Box>
        }
        header={
          <Header
            variant="h2"
            description="Contacts that are eligible and ready for the next scheduling window."
            actions={<Button onClick={onOpenCampaigns}>Review audiences</Button>}
          >
            Next-call queue
          </Header>
        }
        cardDefinition={{
          header: (target) => formatSourceLabel(target.source_model),
          sections: [
            {
              id: 'readiness',
              header: 'Readiness',
              content: (target) => <VoiceSalesStatus status={target.eligibility_status} />,
            },
            {
              id: 'scheduled',
              header: 'Scheduled',
              content: (target) =>
                target.scheduled_at ? formatDateStringWithTime(target.scheduled_at) : 'Next available window',
            },
            {
              id: 'compliance',
              header: 'Safety check',
              content: (target) => (
                <span style={{ whiteSpace: 'pre-line' }}>{formatComplianceMetadata(target)}</span>
              ),
            },
          ],
        }}
      />

      <Cards
        ariaLabels={{ itemSelectionLabel: (_, call) => call.status, selectionGroupLabel: 'Recent activity' }}
        items={calls.slice(0, 5)}
        trackBy="uuid"
        loading={callsState.isLoading}
        loadingText="Loading recent activity"
        cardsPerRow={[{ cards: 1 }, { minWidth: 700, cards: 2 }]}
        empty={<Box textAlign="center">No calls have been attempted yet.</Box>}
        header={
          <Header
            variant="h2"
            description="The latest outbound attempts and outcomes."
            actions={<Button onClick={onOpenCalls}>Review all calls</Button>}
          >
            Recent activity
          </Header>
        }
        cardDefinition={{
          header: (call) => campaignNames.get(call.campaign_id) ?? 'Voice sales call',
          sections: [
            {
              id: 'status',
              header: 'Status',
              content: (call) => <VoiceSalesStatus status={call.status} error={call.error} />,
            },
            {
              id: 'outcome',
              header: 'Outcome',
              content: (call) => <VoiceSalesStatus status={call.outcome} />,
            },
            {
              id: 'summary',
              header: 'Call',
              content: (call) => (
                <SpaceBetween size="xxxs">
                  <PhoneNumber phoneNumber={call.display_phone_number} />
                  <Box variant="small" color="text-body-secondary">
                    {formatDuration(call.duration_seconds)} · {formatDateStringWithTime(call.created_at)}
                  </Box>
                </SpaceBetween>
              ),
            },
            {
              id: 'action',
              content: (call) => <Button onClick={() => onOpenCall(call.uuid)}>View call</Button>,
            },
          ],
        }}
      />

      <Container
        header={
          <Header variant="h2" description="Secondary controls that protect campaign scheduling and follow-up.">
            Safety and configuration
          </Header>
        }
      >
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box color="text-label">Eligible contacts</Box>
            {targetsState.isLoading ? <Spinner /> : <Box>{nextTargets.length}</Box>}
          </div>
          <div>
            <Box color="text-label">Suppression checks</Box>
            {targetsState.isLoading ? <Spinner /> : <Box>{suppressionChecks} recorded</Box>}
          </div>
          <div>
            <Box color="text-label">Phone type</Box>
            {targetsState.isLoading ? <Spinner /> : <Box>{phoneTypesKnown} classified</Box>}
          </div>
          <div>
            <Box color="text-label">Booking</Box>
            <StatusIndicator type="stopped">Disabled until approved</StatusIndicator>
          </div>
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  );
}